import { COMPANY } from '@components/legal/content/sharedContent';
import PaymentMethods from '@components/shared/PaymentMethods';
import { DEFAULT_LANGUAGE, ROUTER_NAME } from '@consts';
import { setupForDark } from '@theme';
import classNames from 'classnames';
import Link from 'next/link';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

export default function LandingPageFooter({
  variant = 'page'
}: {
  variant?: 'page' | 'modal';
}): ReactElement {
  const isModal = variant === 'modal';
  // landing pages are Polish-only marketing surfaces; inside a modal the footer follows the app locale
  const { t } = useTranslation(undefined, isModal ? undefined : { lng: DEFAULT_LANGUAGE });

  const links: { key: string; href: string; label: string }[] = [
    { key: 'terms', href: ROUTER_NAME.TERMS, label: t('domains.legal.footer.terms') },
    { key: 'privacy', href: ROUTER_NAME.PRIVACY_POLICY, label: t('domains.legal.footer.privacy') },
    { key: 'dpo', href: ROUTER_NAME.DATA_PROTECTION_OFFICER, label: t('domains.legal.footer.dpo') },
    { key: 'returns', href: ROUTER_NAME.RETURN_POLICY, label: t('domains.legal.footer.returns') },
    { key: 'contact', href: ROUTER_NAME.CONTACT, label: t('domains.legal.footer.contact') }
  ];

  const visibleLinks = isModal
    ? links.filter((link) => ['terms', 'returns'].includes(link.key))
    : links;

  const secondaryText = isModal ? setupForDark('text-secondary') : 'text-secondary';
  const tertiaryText = isModal ? setupForDark('text-tertiary') : 'text-tertiary';
  const linkClass = classNames(
    'underline underline-offset-2 transition-colors',
    isModal
      ? classNames(setupForDark('text-secondary'), 'hover:opacity-70')
      : 'text-secondary hover:text-white'
  );

  return (
    <footer
      className={classNames(
        'w-full',
        isModal
          ? classNames('pt-md border-t', setupForDark('border-primary'))
          : 'bg-modal-primary px-md py-xl sm:px-xl'
      )}
    >
      <div
        className={classNames(
          'flex w-full flex-col',
          isModal
            ? 'gap-md'
            : 'mx-auto max-w-6xl gap-lg md:flex-row md:items-start md:justify-between'
        )}
      >
        <div className='flex flex-col gap-sm'>
          {!isModal && (
            <div className={classNames('P5R flex flex-col gap-xxs', secondaryText)}>
              {/* Company registration data — fixed legal identifiers, not translatable. */}
              <p>
                <span className='P5B text-white'>{COMPANY.legalNameShort}</span>
                {` · ${COMPANY.street}, ${COMPANY.postalCity}`}
              </p>
              <p>{COMPANY.courtFull}</p>
              <p>{`KRS ${COMPANY.krs} · NIP ${COMPANY.nip} · REGON ${COMPANY.regon} · kapitał zakładowy: ${COMPANY.shareCapital}`}</p>
            </div>
          )}
          <div className={classNames('P5R flex flex-wrap items-center gap-xs', secondaryText)}>
            {visibleLinks.map((link, i) => (
              <span key={link.key} className='inline-flex items-center gap-xs'>
                {i > 0 && <span className={tertiaryText}>·</span>}
                <Link
                  href={link.href}
                  className={linkClass}
                  {...(isModal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                >
                  {link.label}
                </Link>
              </span>
            ))}
          </div>
        </div>
        <div className={classNames('flex flex-col gap-xs', !isModal && 'md:items-end')}>
          <span className={classNames('P5R', tertiaryText)}>
            {t('domains.legal.footer.securePayments')}
          </span>
          <PaymentMethods />
        </div>
      </div>
    </footer>
  );
}
